import { useState, useEffect, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import {
  bulletinBoardService,
  BulletinBoardEntry,
  CreateBulletinBoardEntryDto,
  UpdateBulletinBoardEntryDto,
} from '../../services/bulletin-board.service'
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card'
import { Button } from '../../components/ui/button'
import { Loader2, Plus, Edit, Trash2, X } from 'lucide-react'
import { useAuth } from '../../auth/auth.context'
import { PageLayout } from '../../components/layout'
import { Container } from '../../components/ui/container'
import { useLocale } from '../../hooks/use-locale'

export default function BulletinBoardPage() {
  const { t } = useTranslation()
  const { formatDateTime } = useLocale()
  const { user } = useAuth()
  const [entries, setEntries] = useState<BulletinBoardEntry[]>([])
  const [loading, setLoading] = useState(true)
  const [modalOpen, setModalOpen] = useState(false)
  const [editingEntry, setEditingEntry] = useState<BulletinBoardEntry | null>(null)
  const [content, setContent] = useState('')
  const [submitting, setSubmitting] = useState(false)

  const loadEntries = useCallback(async () => {
    if (!user?.companyId) return
    try {
      setLoading(true)
      const data = await bulletinBoardService.getAllByCompany(user.companyId)
      setEntries(data)
    } catch (error) {
      console.error('Failed to load bulletin board entries:', error)
    } finally {
      setLoading(false)
    }
  }, [user?.companyId])

  useEffect(() => {
    loadEntries()
  }, [loadEntries])

  const handleCreate = () => {
    setEditingEntry(null)
    setContent('')
    setModalOpen(true)
  }

  const handleEdit = (entry: BulletinBoardEntry) => {
    setEditingEntry(entry)
    setContent(entry.content)
    setModalOpen(true)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!content.trim()) return

    try {
      setSubmitting(true)
      if (editingEntry) {
        const dto: UpdateBulletinBoardEntryDto = { content }
        await bulletinBoardService.update(editingEntry.id, dto)
      } else {
        const dto: CreateBulletinBoardEntryDto = { content }
        await bulletinBoardService.create(dto)
      }
      setModalOpen(false)
      setContent('')
      setEditingEntry(null)
      await loadEntries()
    } catch (error) {
      console.error('Failed to save entry:', error)
      alert(t('errors.saveBulletinEntry'))
    } finally {
      setSubmitting(false)
    }
  }

  const handleDelete = async (id: string) => {
    if (!confirm(t('bulletin.deleteConfirm'))) return

    try {
      await bulletinBoardService.delete(id)
      await loadEntries()
    } catch (error) {
      console.error('Failed to delete entry:', error)
      alert(t('errors.deleteBulletinEntry'))
    }
  }

  if (loading) {
    return (
      <div className='flex items-center justify-center h-full'>
        <Loader2 className='w-8 h-8 animate-spin text-blue-500' />
      </div>
    )
  }

  return (
    <PageLayout
      isLoading={loading}
      loadingMessage={t('loading.dashboard')}
      errorMessage={t('errors.loadDashboard')}
    >
      <Container>
        <div className='flex items-center justify-between'>
          <div>
            <h1 className='text-xl font-semibold text-neutral-900'>{t('bulletin.title')}</h1>
            <p className='text-sm text-neutral-500 mt-0.5'>{t('bulletin.subtitle')}</p>
          </div>
          <Button onClick={handleCreate}>
            <Plus className='w-4 h-4 mr-2' />
            {t('bulletin.newEntry')}
          </Button>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>{t('bulletin.entriesTitle')}</CardTitle>
          </CardHeader>
          <CardContent>
            {entries.length === 0 ? (
              <div className='text-center py-12 text-gray-500'>
                <p>{t('bulletin.empty')}</p>
                <p className='text-sm mt-2'>{t('bulletin.emptyHint')}</p>
              </div>
            ) : (
              <div className='space-y-4'>
                {entries.map(entry => (
                  <div
                    key={entry.id}
                    className='border rounded-lg p-4 hover:shadow-md transition-shadow'
                  >
                    <div className='flex justify-between items-start'>
                      <div className='flex-1'>
                        <p className='text-base mb-2'>{entry.content}</p>
                        <div className='flex items-center gap-4 text-sm text-gray-500'>
                          <span>{t('common.createdBy', { name: entry.createdByName })}</span>
                          <span>•</span>
                          <span>
                            {formatDateTime(entry.createdAt, {
                              day: '2-digit',
                              month: '2-digit',
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit',
                            })}
                          </span>
                          {entry.createdAt !== entry.updatedAt && (
                            <>
                              <span>•</span>
                              <span>
                                {t('common.editedAt', {
                                  date: formatDateTime(entry.updatedAt, {
                                    day: '2-digit',
                                    month: '2-digit',
                                    year: 'numeric',
                                    hour: '2-digit',
                                    minute: '2-digit',
                                  }),
                                })}
                              </span>
                            </>
                          )}
                        </div>
                      </div>
                      <div className='flex gap-2 ml-4'>
                        <Button variant='outline' size='sm' onClick={() => handleEdit(entry)}>
                          <Edit className='w-4 h-4' />
                        </Button>
                        <Button
                          variant='destructive'
                          size='sm'
                          onClick={() => handleDelete(entry.id)}
                        >
                          <Trash2 className='w-4 h-4' />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {modalOpen && (
          <div className='fixed inset-0 flex items-center backdrop-blur-xs justify-center z-50'>
            <div className='shadow-xl max-w-2xl w-full mx-4 border border-gray-200 rounded-lg bg-white overflow-hidden'>
              <div className='flex justify-between items-center p-6 border-b'>
                <h2 className='text-xl font-semibold'>
                  {editingEntry ? t('bulletin.editEntry') : t('bulletin.newEntry')}
                </h2>
                <button
                  onClick={() => setModalOpen(false)}
                  className='text-gray-400 hover:text-gray-600'
                >
                  <X className='w-5 h-5' />
                </button>
              </div>
              <form onSubmit={handleSubmit}>
                <div className='p-6'>
                  <label htmlFor='content' className='block text-sm font-medium mb-2'>
                    {t('common.message')}
                  </label>
                  <textarea
                    id='content'
                    value={content}
                    onChange={e => setContent(e.target.value)}
                    className='w-full border rounded-md p-3 min-h-[120px] focus:outline-none focus:ring-2 focus:ring-blue-500'
                    placeholder={t('bulletin.messagePlaceholder')}
                    required
                    disabled={submitting}
                  />
                </div>
                <div className='flex justify-end gap-3 p-6 border-t bg-gray-50'>
                  <Button
                    type='button'
                    variant='outline'
                    onClick={() => setModalOpen(false)}
                    disabled={submitting}
                  >
                    {t('common.cancel')}
                  </Button>
                  <Button type='submit' disabled={submitting || !content.trim()}>
                    {submitting ? (
                      <>
                        <Loader2 className='w-4 h-4 mr-2 animate-spin' />
                        {t('common.saving')}
                      </>
                    ) : (
                      t('common.save')
                    )}
                  </Button>
                </div>
              </form>
            </div>
          </div>
        )}
      </Container>
    </PageLayout>
  )
}
